/**
 * SMS incidents service over the typed `@tiaki/http-client` per ADR 113.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export interface Incident {
  id: number;
  reference_id: string;
  type: number;
  school: number;
  initiator: number | null;
  created: string;
  type_name: string;
  icon: string | null;
}

export interface IncidentListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Incident[];
}

export interface IncidentFilters {
  school?: number;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export async function getIncidentDetail(id: number): Promise<Incident> {
  const result = await client.GET('/api/v2/incident/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as Incident;
}

export async function getIncidents(
  filters: IncidentFilters = {},
): Promise<IncidentListResponse> {
  const result = await client.GET('/api/v2/incident/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as IncidentListResponse;
}

export interface IncidentEvent {
  id: string;
  created_at: string;
  event_type: 'chat' | 'system' | 'guidance';
  subtype: string | null;
  author_name: string;
  author_id: number | null;
  content: string;
  has_attachments: boolean;
}

export interface IncidentEventsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IncidentEvent[];
}

export interface IncidentEventsParams {
  limit?: number;
  offset?: number;
}

export async function getIncidentEvents(
  id: number,
  params: IncidentEventsParams = {},
): Promise<IncidentEventsResponse> {
  // Endpoint not in the typed schema: use the ky escape hatch and shape
  // the response manually.
  const search = new URLSearchParams();
  if (params.limit != null) search.set('limit', String(params.limit));
  if (params.offset != null) search.set('offset', String(params.offset));
  const query = search.toString();
  const url = `api/v2/incident/${id}/events/${query ? `?${query}` : ''}`;
  return (await client.ky.get(url).json()) as IncidentEventsResponse;
}
